import { useCallback, useEffect, useRef, useState } from 'react';
import { userProfileService } from '../services/userProfileService';

export function useUserProfileSummary(userId, options = {}) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const profileSource = options?.source === 'owner'
    ? 'owner'
    : (options?.source === 'internet' ? 'internet' : 'public');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadProfile = useCallback(async (loadOptions = {}) => {
    if (!userId) {
      setProfile(null);
      return null;
    }
    setIsLoading(true);
    setError(null);
    try {
      const hasLoadOptions = loadOptions
        && typeof loadOptions === 'object'
        && Object.keys(loadOptions).length > 0;
      const loadWithOptionalOptions = (loader) => (
        hasLoadOptions
          ? loader(userId, loadOptions)
          : loader(userId)
      );
      const profileLoader = profileSource === 'owner'
        ? userProfileService.getOwnProfile
        : (
          profileSource === 'internet'
            ? userProfileService.getInternetProfile
            : userProfileService.getProfile
        );
      const data = await loadWithOptionalOptions(profileLoader);
      if (!mountedRef.current) {
        return null;
      }
      setProfile(data);
      return data;
    } catch (err) {
      if (!mountedRef.current) {
        return null;
      }
      setError(err);
      throw err;
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [profileSource, userId]);

  const setProfileIfMounted = useCallback((nextProfile) => {
    if (mountedRef.current) {
      setProfile(nextProfile);
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      return;
    }
    loadProfile().catch(() => {
      // error already captured in state
    });
  }, [userId, loadProfile]);

  return {
    profile,
    isLoading,
    error,
    refresh: loadProfile,
    setProfile: setProfileIfMounted,
  };
}

export default useUserProfileSummary;
